import { HttpClient, HttpEventType, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { FilesService } from '@careapp-group/ng-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, EMPTY } from 'rxjs';
import { catchError, filter, take, takeUntil, tap, timeout, withLatestFrom } from 'rxjs/operators';
import { CAAction } from '../actions/index';
import * as selectors from '../reducers/index';
import * as actions from './file-upload.actions';
import { RollbarService } from 'angular-rollbar';
import { CAREAPP_ASSETS, FILE_UPLOAD_MAX_SIZE, FilePurposes } from '@careapp-group/core';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class FileUploadEffects {
  uploadStart = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.RequestFileUploadStartAction),
        withLatestFrom(
          this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
          this.store.select(selectors.getSelectedId(selectors.TENANTS)),
        ),
        tap(async ([source, organisationId, tenantId]) => {
          try {
            await this._performFileUpload(source, organisationId, tenantId ?? source.tenantId);
          } catch (error) {
            this._handleUploadError(source.id, error);
          }
        }),
      ),
    { dispatch: false },
  );

  uploadRetry = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.RequestFileUploadRetryAction),
        withLatestFrom(
          this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
          this.store.select(selectors.getSelectedId(selectors.TENANTS)),
          this.store.select(selectors.getFileUploadState),
        ),
        tap(async ([action, organisationId, tenantId, uploadState]) => {
          const fileUploadState = uploadState[action.id];

          if (fileUploadState && fileUploadState.file) {
            const source: Parameters<typeof actions.RequestFileUploadStartAction>[0] = {
              id: action.id,
              file: fileUploadState.file,
              url: fileUploadState.url,
            };
            try {
              await this._performFileUpload(source, organisationId, tenantId);
            } catch (error) {
              this._handleUploadError(action.id, error);
            }
          }
        }),
      ),
    { dispatch: false },
  );

  // Extracted file upload logic for use in upload and retry effects.
  async _performFileUpload(
    source: Parameters<typeof actions.RequestFileUploadStartAction>[0],
    organisationId: string,
    tenantId: string,
  ) {
    // A member badge must be uploaded as part of CareApp assets.
    // Note: Purpose may not exist.
    tenantId = source.purpose == FilePurposes.memberBadge ? CAREAPP_ASSETS : tenantId;

    const target = await this.filesApi
      .requestUpload({
        uploadRequestDto: {
          tenantId,
          organisationId: organisationId ?? source.organisationId,
          mimeType: source.file.type,
          purpose: source.purpose,
          modelName: source.modelName,
          name: source.file.name,
          size: source.file.size,
        },
      })
      .toPromise();

    this.putFile(target.uploadUrl, source.file)
      .pipe(
        takeUntil(
          this.actions$.pipe(
            ofType(actions.RequestFileUploadCancelAction),
            filter((s) => {
              return s.id === source.id;
            }),
          ),
        ),
        catchError((event) => {
          this._handleUploadError(source.id, event);
          return EMPTY;
        }),
      )
      .subscribe(async (event: any) => {
        switch (event.type) {
          case HttpEventType.Response:
            await this.filesApi.setComplete({ id: target.file.id }).toPromise();
            return this.store.dispatch(
              actions.ResponseFileUploadCompleteAction({
                id: source.id,
                response: target.file,
                uploadType: source.uploadType,
              }),
            );
          case HttpEventType.Sent:
          case HttpEventType.UploadProgress:
          case HttpEventType.ResponseHeader:
          case HttpEventType.DownloadProgress:
            return this.store.dispatch(
              actions.ResponseFileUploadProgressAction({
                id: source.id,
                progress: this.progress(event),
              }),
            );
          case actions.RESPONSE_FILE_UPLOAD_ERROR:
            return this.store.dispatch(event);
        }
      });
  }

  // Helper to give descriptive errors for any file upload errors.
  // The fileId will be the temporary ID of the file, because we won't
  // have a server ID yet.
  //
  // Handles:
  // * File upload too large
  // * Network change or connection reset
  //
  // Will give preference to any existing error message from the server.
  _handleUploadError(fileId: string, error: any) {
    this.errorLogger?.error(error);
    let errorMessage = this.translate.instant('fileUpload.error.unknown');

    if (error.error?.error?.message) {
      errorMessage = error.error?.error?.message;
    } else if (error.status === 413) {
      errorMessage = this.translate.instant('fileUpload.error.size', {
        size: FILE_UPLOAD_MAX_SIZE / 1024 / 1024 / 1024,
      });
    } else if (error.status === 0) {
      errorMessage = this.translate.instant('fileUpload.error.network');
    } else if (error.message && error.message.includes('ERR_NETWORK_CHANGED')) {
      errorMessage = this.translate.instant('fileUpload.error.networkChanged');
    } else if (error.message && error.message.includes('ERR_CONNECTION_RESET')) {
      errorMessage = this.translate.instant('fileUpload.error.networkReset');
    }

    this.store.dispatch(
      actions.ResponseFileUploadErrorAction({
        id: fileId,
        error: errorMessage,
        status: error.status,
      }),
    );
  }

  constructor(
    private store: Store<any>,
    private actions$: Actions<CAAction>,
    private http: HttpClient,
    private filesApi: FilesService,
    private translate: TranslateService,
    @Optional() private errorLogger?: RollbarService,
  ) {}

  progress(event) {
    const loaded = event.loaded || 0;
    const total = event.total || 100;
    const percent = (100 * loaded) / total;
    return percent;
  }

  buildFormData(file: File) {
    const formData = new FormData();
    formData.append('file[]', file);
    return formData;
  }

  putFile(url: string, file: File) {
    const headers = new HttpHeaders({ 'Content-Type': file.type });
    return this.http.request(
      new HttpRequest('PUT', url, file, {
        reportProgress: true,
        headers,
        responseType: 'text',
      }),
    );
  }
}
